import { combineReducers, type Reducer } from "redux";
import { type PayloadAction } from "@reduxjs/toolkit";

import * as actionTypes from "./addGroupActionTypes";
import { createReducer } from "utils/reduxUtils";
import ReducerNamespaceTypes from "../../../../../enums/reducer/reducerNamespaceTypes";
import ReducerEntityPrefixTypes from "../../../../../enums/reducer/reducerEntityPrefixTypes";
import { withFetchingChildEntityListItems } from "features/Application/reducers/hoc/withFetchingItems";
import withFilteringItems from "../../../../Application/reducers/hoc/withFilteringItems";
import withSearch from "../../../../Application/reducers/hoc/withSearch";
import { type ICreatedGroupState, type IEnrollmentUsersFilterOptionsState, type IOwnerItemModel } from "../../../types";
import groupMembersInfoSlice from "./slices/groupMembersInfoSlice";
import groupMembersFiltersSlice from "./slices/groupMembersFiltersSlice";
import groupMembersAssignmentSlice from "./slices/groupMembersAssignmentSlice";
import groupMembersAssignmentFiltersSlice from "./slices/groupMembersAssignmentFiltersSlice";
import groupMembersModalsSlice from "./slices/groupMembersModalsSlice";

const namespace = ReducerNamespaceTypes.People;
const entityPrefix = ReducerEntityPrefixTypes.AddGroup;

interface CreateGroup {
  createdGroup: ICreatedGroupState;
  isLoadingGroupInfo: boolean;
  isContentLoading: boolean;
  error: any;
}

const initialState: CreateGroup = {
  isLoadingGroupInfo: false,
  isContentLoading: false,
  error: null,
  createdGroup: {
    name: "",
    description: "",
    isAware: true,
    isVisibleToAll: true,
    isOpen: true,
    isWithAutoEnroll: false,
    ownersIds: [],
    enrolmentRules: [],
    imageUrl: "",
    groupId: -1,
    roleId: -1,
  },
};

const startGroupInfoLoading = (state: CreateGroup) => {
  return {
    ...state,
    isLoadingGroupInfo: true,
    error: null,
  };
};

const groupInfoFailed = (state: CreateGroup, action: PayloadAction<{ error: string }>) => {
  return {
    ...state,
    isLoadingGroupInfo: false,
    error: action.payload.error,
  };
};

const createGroupHandler = () => {
  const { CREATE_GROUP_BEGIN, CREATE_GROUP_FAILURE, CREATE_GROUP_SUCCESS, UPDATE_GROUP_SUCCESS, RESET_GROUP_STATE } =
    actionTypes;

  const updateGroupSucceeded = (state: CreateGroup, action: PayloadAction<any>) => {
    return { ...initialState, createdGroup: action.payload };
  };

  const createGroupSucceeded = (state: CreateGroup, action: PayloadAction<any>) => {
    return { ...initialState, createdGroup: action.payload };
  };

  return {
    [CREATE_GROUP_BEGIN]: startGroupInfoLoading,
    [CREATE_GROUP_FAILURE]: groupInfoFailed,
    [CREATE_GROUP_SUCCESS]: createGroupSucceeded,
    [UPDATE_GROUP_SUCCESS]: updateGroupSucceeded,
    [RESET_GROUP_STATE]: () => initialState,
  };
};

const buildChildEntityReducer = <T extends object = {}>(childEntityPrefix: string) =>
  withFetchingChildEntityListItems<T>(namespace, entityPrefix, childEntityPrefix);

const buildFilteringReducer = (childEntityPrefix: string, reducer: Reducer) =>
  withFilteringItems<IEnrollmentUsersFilterOptionsState>(namespace, `${entityPrefix}__${childEntityPrefix}`, reducer);

const enrollmentUsersList = buildChildEntityReducer(actionTypes.EnrollmentUsers);
const manualSelectionUsersList = buildChildEntityReducer(actionTypes.ManualSelectionUsers);
const ownersSelectionList = buildChildEntityReducer<IOwnerItemModel>(actionTypes.Owners);

const createGroupReducer = createReducer<CreateGroup>(initialState, [createGroupHandler]);

const delimiter = "__";

export const addGroup = combineReducers({
  enrollmentUsersFilterOptions: buildFilteringReducer(actionTypes.EnrollmentUsers, enrollmentUsersList),
  manualUsersFilterOptions: manualSelectionUsersList,
  ownersList: ownersSelectionList,
  createGroup: createGroupReducer,
  membersSearch: withSearch(namespace, entityPrefix.concat(delimiter, actionTypes.ManualSelectionUsers)),
  ownersSearch: withSearch(namespace, entityPrefix.concat(delimiter, actionTypes.Owners)),
  groupMembers: combineReducers({
    modalOptions: groupMembersModalsSlice,
    overview: combineReducers({
      list: groupMembersInfoSlice,
      filter: groupMembersFiltersSlice,
    }),
    assign: combineReducers({
      list: groupMembersAssignmentSlice,
      filter: groupMembersAssignmentFiltersSlice,
    }),
  }),
});

export type AddGroupState = ReturnType<typeof addGroup>;
